import { DependencyList, useEffect, useState } from 'react';

type CmsQueryState<T> = {
  data?: T;
  loading: boolean;
  error?: Error;
};

export const CMS_RETRY_DELAYS_MS = [500, 1500];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetry = async <T, >(
  fetcher: () => Promise<T>,
  isCancelled: () => boolean,
): Promise<T> => {
  for (const delay of CMS_RETRY_DELAYS_MS) {
    try {
      return await fetcher();
    } catch (error) {
      await wait(delay);
      if (isCancelled()) throw error;
    }
  }

  return fetcher();
};

/**
 * Thin React adapter around a CMS repository call. Owns loading/error state and
 * re-runs the fetcher when `deps` change, cancelling stale results on unmount or
 * dependency change. All CMS communication itself lives in `src/cms`.
 */
const useCmsQuery = <T, >(
  fetcher: () => Promise<T>,
  deps: DependencyList,
  options?: { skip?: boolean },
): CmsQueryState<T> => {
  const skip = options?.skip ?? false;
  const [state, setState] = useState<CmsQueryState<T>>({ loading: !skip });

  useEffect(() => {
    if (skip) {
      setState({ data: undefined, loading: false });

      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: undefined }));

    fetchWithRetry(fetcher, () => cancelled)
      .then((data) => {
        if (!cancelled) {
          setState({ data, loading: false });
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setState({ data: undefined, loading: false, error });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, ...deps]);

  return state;
};

export default useCmsQuery;
